/* David Pladen — cart drawer and cart page
 * (sections/dp-cart-drawer.liquid, sections/dp-main-cart.liquid)
 *
 * - <cart-drawer>: the tag Dawn's product-form.js and quick-add.js look for,
 *   so every Dawn add-to-cart re-renders and opens this drawer.
 * - <dp-cart-page>: the same cart on /cart when Cart type = Page.
 * - window.DPCart.add(items, trigger) and any [data-dp-add="variantId"] button
 *   anywhere on the site (sticky bar, collection "+", Instagram lightbox...)
 *   add through the Cart AJAX API and open the drawer.
 * Markup always comes from Liquid via the Section Rendering API.
 */

(() => {
  if (customElements.get('dp-cart-page')) return;

  const FOCUSABLE =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const BUBBLE = 'cart-icon-bubble';

  class DpCart extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.status = this.querySelector('[data-dp-cart-status]');
      this.onClick = this.onClick.bind(this);
      this.onInput = this.onInput.bind(this);
      this.addEventListener('click', this.onClick);
      this.addEventListener('input', this.onInput);
      this.afterRender();
    }

    get content() {
      return this.querySelector('[data-dp-cart-content]');
    }

    /* Every DP cart on the page (drawer and /cart) is re-rendered together */
    static all() {
      return Array.from(document.querySelectorAll('.dp-cart[data-section-id]'));
    }

    static renderAll(sections) {
      DpCart.all().forEach((cart) => cart.render(sections));
    }

    getSectionsToRender() {
      const ids = DpCart.all().map((cart) => cart.dataset.sectionId);
      return [...new Set([this.sectionId, ...ids])].map((id) => ({ id })).concat({ id: BUBBLE });
    }

    /* ---- Actions ---- */

    onClick(event) {
      const qty = event.target.closest('[data-dp-qty]');
      if (qty) {
        const line = qty.closest('[data-dp-line]');
        this.request(
          window.routes.cart_change_url,
          { id: qty.dataset.dpKey, quantity: parseInt(qty.dataset.dpQty, 10) },
          null,
          line
        );
        return;
      }

      const add = event.target.closest('[data-dp-add]');
      if (add) {
        event.preventDefault();
        this.add([{ id: parseInt(add.dataset.dpAdd, 10), quantity: 1 }], add);
        return;
      }

      const scroll = event.target.closest('[data-dp-scroll]');
      if (scroll) {
        const track = this.querySelector('[data-dp-track]');
        const card = track?.querySelector('.ck-uc');
        if (track && card) track.scrollBy({ left: parseInt(scroll.dataset.dpScroll, 10) * (card.offsetWidth + 12) });
      }
    }

    onInput(event) {
      if (!event.target.matches('[data-dp-note]')) return;
      window.clearTimeout(this.noteTimer);
      const note = event.target.value;
      this.noteTimer = window.setTimeout(() => {
        fetch(window.routes.cart_update_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ note }),
        }).catch(() => {});
      }, 400);
    }

    add(items, trigger) {
      return this.request(window.routes.cart_add_url, { items }, this.status?.dataset.added || '', null, trigger);
    }

    async request(url, body, message, line, trigger) {
      this.setAttribute('aria-busy', 'true');
      line?.classList.add('is-loading');
      trigger?.setAttribute('aria-busy', 'true');
      this.clearError();

      try {
        const response = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            ...body,
            sections: this.getSectionsToRender().map((section) => section.id),
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json();

        if (!response.ok || data.status) {
          this.showError(data.description || data.message || window.cartStrings?.error || '');
          return null;
        }

        DpCart.renderAll(data.sections);
        // Quantity changes announce the new total line, e.g. "Total €79,95"
        const total = Array.from(this.querySelector('.ck-t')?.children || []).map((el) => el.textContent.trim()).join(' ');
        this.announce(message || total);
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'dp-cart', cartData: data });
        }
        return data;
      } catch (error) {
        console.error(error);
        this.showError(window.cartStrings?.error || '');
        return null;
      } finally {
        this.removeAttribute('aria-busy');
        line?.classList.remove('is-loading');
        trigger?.removeAttribute('aria-busy');
      }
    }

    /* ---- Rendering ---- */

    render(sections) {
      if (!sections) return;
      const parser = new DOMParser();

      const html = sections[this.sectionId];
      const current = this.content;
      if (html && current) {
        const next = parser.parseFromString(html, 'text/html').querySelector('[data-dp-cart-content]');
        if (next) {
          const focusId = document.activeElement?.dataset?.focusId;
          const hadFocus = this.contains(document.activeElement);
          const track = this.querySelector('[data-dp-track]');
          const scrollLeft = track ? track.scrollLeft : 0;
          current.innerHTML = next.innerHTML;
          const newTrack = this.querySelector('[data-dp-track]');
          if (newTrack) newTrack.scrollLeft = scrollLeft;
          if (hadFocus) {
            const target =
              (focusId && this.querySelector(`[data-focus-id="${CSS.escape(focusId)}"]`)) ||
              this.querySelector('.ck-title');
            target?.focus({ preventScroll: true });
          }
        }
      }

      const bubbleHtml = sections[BUBBLE];
      const bubble = document.getElementById(BUBBLE);
      if (bubbleHtml && bubble) {
        const next = parser.parseFromString(bubbleHtml, 'text/html').querySelector('.shopify-section');
        if (next) bubble.innerHTML = next.innerHTML;
      }

      this.afterRender();
    }

    afterRender() {
      this.bindCarousel();
      this.loadRecommendations();
    }

    bindCarousel() {
      const track = this.querySelector('[data-dp-track]');
      if (!track || track.dataset.bound) return;
      track.dataset.bound = 'true';
      const prev = this.querySelector('[data-dp-scroll="-1"]');
      const next = this.querySelector('[data-dp-scroll="1"]');
      const update = () => {
        if (prev) prev.disabled = track.scrollLeft < 4;
        if (next) next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
      };
      track.addEventListener('scroll', update, { passive: true });
      window.requestAnimationFrame(update);
      update();
    }

    /* Shopify recommendations: fetch this section from /recommendations/products */
    async loadRecommendations() {
      const box = this.querySelector('[data-dp-recs-url]');
      if (!box || box.dataset.loading) return;
      box.dataset.loading = 'true';
      try {
        const response = await fetch(box.dataset.dpRecsUrl);
        const html = await response.text();
        const upsell = new DOMParser()
          .parseFromString(html, 'text/html')
          .querySelector('[data-dp-upsell]:not([data-dp-recs-url])');
        if (upsell && box.isConnected) {
          box.replaceWith(upsell);
          this.bindCarousel();
        } else {
          box.remove();
        }
      } catch (error) {
        box.remove();
      }
    }

    showError(message) {
      const error = this.querySelector('[data-dp-cart-error]');
      if (!error) return;
      error.textContent = message;
      error.hidden = false;
    }

    clearError() {
      const error = this.querySelector('[data-dp-cart-error]');
      if (error) error.hidden = true;
    }

    announce(message) {
      if (!this.status) return;
      this.status.textContent = '';
      window.setTimeout(() => {
        this.status.textContent = message;
      }, 50);
    }
  }

  class DpCartDrawer extends DpCart {
    connectedCallback() {
      super.connectedCallback();
      this.drawer = this.querySelector('.drawer');
      this.onKeydown = this.onKeydown.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      document.addEventListener('keydown', this.onKeydown);
      document.addEventListener('click', this.onDocumentClick);
      const bag = document.getElementById(BUBBLE);
      if (bag) {
        bag.setAttribute('role', 'button');
        bag.setAttribute('aria-haspopup', 'dialog');
      }
    }

    disconnectedCallback() {
      document.removeEventListener('keydown', this.onKeydown);
      document.removeEventListener('click', this.onDocumentClick);
    }

    /* Dawn contract (product-form.js, quick-add.js) */
    setActiveElement(element) {
      this.activeElement = element;
    }

    renderContents(response) {
      DpCart.renderAll(response.sections);
      this.open();
    }

    async add(items, trigger) {
      const data = await super.add(items, trigger);
      if (data) this.open(trigger);
      return data;
    }

    get isOpen() {
      return this.classList.contains('is-open');
    }

    open(trigger) {
      if (trigger && !this.contains(trigger)) this.activeElement = trigger;
      if (this.isOpen) return;
      this.drawer.removeAttribute('inert');
      this.drawer.setAttribute('aria-hidden', 'false');
      this.classList.add('is-open');
      document.documentElement.classList.add('dp-scroll-lock');
      this.querySelector('.ck-x')?.focus({ preventScroll: true });
    }

    close() {
      if (!this.isOpen) return;
      this.classList.remove('is-open');
      this.drawer.setAttribute('aria-hidden', 'true');
      this.drawer.setAttribute('inert', '');
      document.documentElement.classList.remove('dp-scroll-lock');
      const back =
        this.activeElement && this.activeElement.isConnected && this.activeElement !== document.body
          ? this.activeElement
          : document.getElementById(BUBBLE);
      back?.focus({ preventScroll: true });
    }

    onClick(event) {
      if (event.target.closest('[data-dp-cart-close]')) {
        this.close();
        return;
      }
      super.onClick(event);
    }

    onDocumentClick(event) {
      const bag = event.target.closest(`#${BUBBLE}`);
      if (!bag) return;
      event.preventDefault();
      this.open(bag);
    }

    onKeydown(event) {
      if (!this.isOpen) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        this.close();
        return;
      }
      if (event.key !== 'Tab') return;
      const items = Array.from(this.drawer.querySelectorAll(FOCUSABLE)).filter((el) => el.offsetParent !== null);
      if (!items.length) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (!this.drawer.contains(active)) {
        event.preventDefault();
        first.focus();
      } else if (event.shiftKey && (active === first || active.classList.contains('ck-title'))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  customElements.define('dp-cart-page', DpCart);
  if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', DpCartDrawer);

  /* Site-wide add-to-cart for DP sections */
  window.DPCart = {
    get element() {
      return document.querySelector('cart-drawer.dp-cart') || document.querySelector('dp-cart-page');
    },
    async add(items, trigger) {
      const cart = this.element;
      if (cart) return cart.add(items, trigger);
      // Cart type = Page and not on /cart: add, then go to the cart page.
      const response = await fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items }),
      });
      if (response.ok) window.location = window.routes.cart_url;
      return response.json();
    },
    open() {
      this.element?.open?.();
    },
  };

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-dp-add]');
    if (!button || button.closest('.dp-cart')) return;
    event.preventDefault();
    const quantity = parseInt(button.dataset.dpQuantity || '1', 10);
    window.DPCart.add([{ id: parseInt(button.dataset.dpAdd, 10), quantity }], button);
  });

  document.addEventListener('dp:cart:add', (event) => {
    window.DPCart.add(event.detail.items, event.detail.trigger);
  });
})();